import { jsPDF } from 'jspdf';

interface Player {
  id: string;
  firstName?: string;
  lastName?: string;
  user?: {
    firstName?: string;
    lastName?: string;
  };
  academyName?: string;
}

interface AssessmentItem {
  id: string;
  name: string;
  category: string;
}

interface BasicPerformanceItem {
  id: string;
  name: string;
  category: string;
  rating: number;
  notes: string;
  evaluationPeriod: string;
  coachId: string;
  playerId: string;
  notApplicable?: boolean;
}

// Helper function to convert hex to RGB
const hexToRgb = (hex: string): number[] => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : [0, 0, 0];
};

// Get category color
const getCategoryColor = (category: string) => {
  const colors: { [key: string]: { bg: string; text: string } } = {
    'Footwork': { bg: '#e8f5e8', text: '#2e7d32' },
    'Grip and Rotation': { bg: '#fff8e1', text: '#f57f17' },
    'Forehand Shots': { bg: '#e3f2fd', text: '#1565c0' },
    'Backhand Shots': { bg: '#e1f5fe', text: '#0277bd' },
    'Fitness': { bg: '#f3e5f5', text: '#7b1fa2' },
    'Follow Instructions': { bg: '#e0f2f1', text: '#00695c' },
    'Interest': { bg: '#fce4ec', text: '#c2185b' },
    'Forehand Strokes': { bg: '#e3f2fd', text: '#1565c0' },
    'Backhand Strokes': { bg: '#e1f5fe', text: '#0277bd' },
    'Behavioural skills': { bg: '#fff3e0', text: '#e65100' },
  };
  return colors[category] || { bg: '#f5f5f5', text: '#666' };
};

// Get period label
const getPeriodLabel = (period: string): string => {
  if (period.startsWith('month-')) {
    const monthIndex = parseInt(period.split('-')[1]) - 1;
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return monthNames[monthIndex];
  } else if (period.startsWith('quarter-')) {
    const quarterNum = period.split('-')[1];
    return `Q${quarterNum}`;
  } else if (period === 'yearly') {
    return 'Yearly';
  }
  return period;
};

// Calculate date range
const calculateDateRange = (period: string, year: number) => {
  const formatDate = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  
  if (period.startsWith('month-')) {
    const monthIndex = parseInt(period.split('-')[1]) - 1;
    const startDate = new Date(year, monthIndex, 1);
    const endDate = new Date(year, monthIndex + 1, 0);
    return {
      start: formatDate(startDate),
      end: formatDate(endDate)
    };
  } else if (period.startsWith('quarter-')) {
    const quarterNum = parseInt(period.split('-')[1]);
    const quarterStartMonth = (quarterNum - 1) * 3;
    const start = new Date(year, quarterStartMonth, 1);
    const end = new Date(year, quarterStartMonth + 3, 0);
    return {
      start: formatDate(start),
      end: formatDate(end)
    };
  } else if (period === 'yearly') {
    const start = new Date(year, 0, 1);
    const end = new Date(year, 11, 31);
    return {
      start: formatDate(start),
      end: formatDate(end)
    };
  } else {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const start = new Date(currentYear, currentMonth, 1);
    const end = new Date(currentYear, currentMonth + 1, 0);
    return {
      start: formatDate(start),
      end: formatDate(end)
    };
  }
};

export const generateManualAssessmentForm = async (
  player: Player,
  selectedSport: string,
  assessmentType: 'high-level' | 'master-training' | 'low-level' | 'fitness' | 'drill',
  coachName: string = 'N/A',
  trainingLevel?: 'beginner' | 'intermediate' | 'advanced' | 'pro'
) => {
  try {
    // Fetch assessment items from API
    const normalizedAssessmentType = assessmentType === 'low-level' ? 'master-training' : assessmentType;
    const levelQuery = normalizedAssessmentType === 'master-training' && trainingLevel ? `&level=${trainingLevel}` : '';
    const url = `/api/performance/assessment-items/${selectedSport}/${normalizedAssessmentType}?t=${Date.now()}${levelQuery}`;
    const response = await fetch(url, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    
    if (!result.success || !result.data || !Array.isArray(result.data)) {
      throw new Error('No assessment items found');
    }

    const assessmentItems: AssessmentItem[] = result.data;
    
    if (assessmentItems.length === 0) {
      alert(`No assessment skills defined for ${selectedSport}. Cannot generate form.`);
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const footerHeight = 15;
    const headerHeight = 20;
    let yPosition = headerHeight;
    const rowHeight = 7;
    const tableStartX = margin;
    let tableStartY = margin;

    // Header with branding
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Encycdata', margin, 8);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('EncycGames', margin, 13);
    
    // Assessment title with sport name
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const sportName = selectedSport || 'Badminton';
    let assessmentTitle = '';
    if (normalizedAssessmentType === 'master-training') {
      const levelLabel = trainingLevel ? `${trainingLevel.charAt(0).toUpperCase()}${trainingLevel.slice(1)} ` : '';
      assessmentTitle = `${sportName} - Manual ${levelLabel}Skill Assessment Form`;
    } else if (assessmentType === 'high-level') {
      assessmentTitle = `${sportName} - Manual High-level Assessment Form`;
    } else if (assessmentType === 'fitness') {
      assessmentTitle = `${sportName} - Manual Fitness Assessment Form`;
    } else if (assessmentType === 'drill') {
      assessmentTitle = `${sportName} - Manual Drill Assessment Form`;
    }
    doc.text(
      assessmentTitle,
      pageWidth / 2,
      yPosition,
      { align: 'center' }
    );
    yPosition += 8;

    // Header Information
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const playerName = `${player?.firstName || player?.user?.firstName || ''} ${player?.lastName || player?.user?.lastName || ''}`.trim();
    const academyName = player?.academyName || 'N/A';
    const reportDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const evaluationPeriod = 'month-1'; // Default to January
    const evaluationYear = new Date().getFullYear();
    const currentDateRange = calculateDateRange(evaluationPeriod, evaluationYear);
    
    doc.text(`Player: ${playerName}`, margin, yPosition);
    // Show "Evaluated By" instead of "Coach", and use "self" for self-evaluations
    // Note: For manual forms, we need to determine if it's self-evaluation based on context
    // For now, if coachName is 'N/A' or empty, show 'self', otherwise show coach name
    const evaluatedBy = (!coachName || coachName === 'N/A') ? 'self' : coachName;
    doc.text(`Evaluated By: ${evaluatedBy}`, pageWidth / 2, yPosition);
    yPosition += 5;
    doc.text(`Academy: ${academyName}`, margin, yPosition);
    doc.text(`Date: ${reportDate}`, pageWidth / 2, yPosition);
    yPosition += 5;
    doc.text(`Period: ${currentDateRange.start} to ${currentDateRange.end} (${getPeriodLabel(evaluationPeriod)} ${evaluationYear})`, margin, yPosition);
    yPosition += 8;

    // Rating Scale Reference Information
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Rating Scale Reference (For Information Only):', margin, yPosition);
    yPosition += 6;
    
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    
    // Calculate the maximum width of level names for alignment
    const levelNames = [
      'Beginner (1-2):',
      'Intermediate (2-3):',
      'Advanced (3-4):',
      'Master (4-5):'
    ];
    doc.setFont('helvetica', 'bold');
    const maxLevelWidth = Math.max(...levelNames.map(name => doc.getTextWidth(name)));
    const definitionStartX = margin + 8 + maxLevelWidth + 5;
    
    // Beginner (1-2)
    doc.setFillColor(244, 67, 54);
    doc.rect(margin, yPosition - 2, 3, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('Beginner (1-2):', margin + 8, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Learning basic rules and shots', definitionStartX, yPosition);
    yPosition += 5;
    
    // Intermediate (2-3)
    doc.setFillColor(255, 152, 0);
    doc.rect(margin, yPosition - 2, 3, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('Intermediate (2-3):', margin + 8, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Can perform upto 5 consistent strokes but lacking accuracy and perfection', definitionStartX, yPosition);
    yPosition += 5;
    
    // Advanced (3-4)
    doc.setFillColor(33, 150, 243);
    doc.rect(margin, yPosition - 2, 3, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('Advanced (3-4):', margin + 8, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Can play 15 or more consistent strokes and do long rallies with different shots', definitionStartX, yPosition);
    yPosition += 5;
    
    // Master (4-5)
    doc.setFillColor(76, 175, 80);
    doc.rect(margin, yPosition - 2, 3, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text('Master (4-5):', margin + 8, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text('Aggressive, Accurate and consistently performing in varying conditions', definitionStartX, yPosition);
    yPosition += 8;

    // Table column widths
    const colWidths = {
      skill: (pageWidth - 2 * margin) * 0.38,
      notApplicable: (pageWidth - 2 * margin) * 0.10,
      rating: (pageWidth - 2 * margin) * 0.18,
      performance: (pageWidth - 2 * margin) * 0.27,
      smiley: (pageWidth - 2 * margin) * 0.07
    };
    
    const drawTableRow = (x: number, y: number, lineWidth: number = 0.2) => {
      doc.setLineWidth(lineWidth);
      let currentX = x;
      const lineY = y + rowHeight;
      const tableEndX = x + (pageWidth - 2 * margin);
      
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.skill;
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.notApplicable;
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.rating;
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.performance;
      doc.line(currentX, y, currentX, lineY);
      currentX += colWidths.smiley;
      doc.line(currentX, y, currentX, lineY);
      
      doc.line(x, lineY, tableEndX, lineY);
    };

    const drawSectionBorder = (x: number, y: number, height: number, lineWidth: number = 0.8) => {
      doc.setLineWidth(lineWidth);
      const endX = x + (pageWidth - 2 * margin);
      const endY = y + height;
      
      doc.line(x, y, endX, y);
      doc.line(endX, y, endX, endY);
      doc.line(endX, endY, x, endY);
      doc.line(x, endY, x, y);
    };

    const drawTableHeader = (y: number) => {
      const headerY = y;
      
      doc.setFillColor(245, 245, 245);
      doc.rect(tableStartX, headerY, pageWidth - 2 * margin, rowHeight, 'F');
      
      doc.setLineWidth(0.5);
      doc.line(tableStartX, headerY, tableStartX + (pageWidth - 2 * margin), headerY);
      
      drawTableRow(tableStartX, headerY, 0.3);
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      let textX = tableStartX;
      // Dynamic column header based on assessment type
      const itemColumnHeader = assessmentType === 'fitness' ? 'Fitness Item' : 
                              assessmentType === 'drill' ? 'Drill Item' : 
                              'Skill';
      doc.text(itemColumnHeader, textX + colWidths.skill / 2, headerY + 5, { align: 'center' });
      textX += colWidths.skill;
      doc.text('N/A', textX + colWidths.notApplicable / 2, headerY + 5, { align: 'center' });
      textX += colWidths.notApplicable;
      doc.text('Rating', textX + colWidths.rating / 2, headerY + 5, { align: 'center' });
      textX += colWidths.rating;
      doc.text('Performance', textX + colWidths.performance / 2, headerY + 5, { align: 'center' });
      textX += colWidths.performance;
      doc.text('Smiley', textX + colWidths.smiley / 2, headerY + 5, { align: 'center' });
    };

    const drawTableDataRow = (item: BasicPerformanceItem, y: number, categoryColor?: { bg: number[], text: number[] }) => {
      const rowY = y;
      
      drawTableRow(tableStartX, rowY, 0.2);
      
      if (categoryColor) {
        doc.setFillColor(categoryColor.bg[0], categoryColor.bg[1], categoryColor.bg[2]);
        doc.rect(tableStartX, rowY, pageWidth - 2 * margin, rowHeight, 'F');
        drawTableRow(tableStartX, rowY, 0.2);
      }
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      let textX = tableStartX + 2;
      
      const skillName = doc.splitTextToSize(item.name, colWidths.skill - 4);
      doc.text(skillName, textX, rowY + 5);
      
      // Not Applicable checkbox (empty box for manual filling)
      textX += colWidths.skill + 2;
      doc.rect(textX, rowY + 2, 3, 3);
      
      // Rating (empty boxes for manual filling - no stars filled)
      textX += colWidths.notApplicable + 2;
      for (let i = 0; i < 5; i++) {
        doc.rect(textX + i * 4, rowY + 2, 3, 3);
      }
      
      // Performance level (blank for manual filling)
      textX += colWidths.rating + 2;
      // Leave blank for manual filling
      
      // Smiley (blank for manual filling)
      textX += colWidths.performance + 2;
      // Leave blank for manual filling
    };

    const addFooter = (doc: jsPDF, pageWidth: number, pageHeight: number, footerHeight: number) => {
      const footerY = pageHeight - footerHeight;
      
      // Draw footer line
      doc.setLineWidth(0.5);
      doc.line(10, footerY, pageWidth - 10, footerY);
      
      // Footer branding
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Encycdata', 10, footerY + 5);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('EncycGames', 10, footerY + 10);
      
      // Page number
      const pageNum = doc.getCurrentPageInfo().pageNumber;
      const totalPages = doc.getNumberOfPages();
      doc.setFontSize(8);
      doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth / 2, footerY + 7, { align: 'center' });
      
      // Date
      const footerDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      doc.text(footerDate, pageWidth - 10, footerY + 7, { align: 'right' });
    };

    // Start table
    tableStartY = yPosition;
    drawTableHeader(tableStartY);
    yPosition += rowHeight;

    // Convert assessment items to BasicPerformanceItem format
    const predefinedItems = assessmentItems.map((item, index) => ({
      id: `skill-${index}`,
      name: item.name,
      category: item.category,
      rating: 0,
      notes: '',
      evaluationPeriod: evaluationPeriod,
      coachId: '',
      playerId: player.id,
      notApplicable: false
    }));

    // Group by category for low-level, fitness, and drill assessments
    if (normalizedAssessmentType === 'master-training' || assessmentType === 'fitness' || assessmentType === 'drill') {
      const groupedItems = predefinedItems.reduce((acc, item) => {
        if (!acc[item.category]) {
          acc[item.category] = [];
        }
        acc[item.category].push(item);
        return acc;
      }, {} as { [key: string]: BasicPerformanceItem[] });

      Object.entries(groupedItems).forEach(([category, items]) => {
        if (yPosition + rowHeight * 2 > pageHeight - footerHeight) {
          addFooter(doc, pageWidth, pageHeight, footerHeight);
          doc.addPage();
          yPosition = headerHeight;
          tableStartY = yPosition;
          drawTableHeader(tableStartY);
          yPosition += rowHeight;
        }

        yPosition += 2;
        const categoryStartY = yPosition;
        const categoryColor = getCategoryColor(category);
        const bgColor = hexToRgb(categoryColor.bg);
        const textColor = hexToRgb(categoryColor.text);
        
        doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
        doc.rect(tableStartX, yPosition, pageWidth - 2 * margin, rowHeight, 'F');
        
        doc.setLineWidth(0.5);
        doc.line(tableStartX, yPosition, tableStartX + (pageWidth - 2 * margin), yPosition);
        doc.line(tableStartX, yPosition + rowHeight, tableStartX + (pageWidth - 2 * margin), yPosition + rowHeight);
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(textColor[0], textColor[1], textColor[2]);
        doc.text(category.toUpperCase(), pageWidth / 2, yPosition + 5, { align: 'center' });
        doc.setTextColor(0, 0, 0);
        yPosition += rowHeight;

        items.forEach((item) => {
          if (yPosition + rowHeight > pageHeight - footerHeight) {
            const sectionHeight = yPosition - categoryStartY;
            drawSectionBorder(tableStartX, categoryStartY, sectionHeight, 0.8);
            
            addFooter(doc, pageWidth, pageHeight, footerHeight);
            doc.addPage();
            yPosition = headerHeight;
            tableStartY = yPosition;
            drawTableHeader(tableStartY);
            yPosition += rowHeight;
            
            const newCategoryStartY = yPosition;
            yPosition += 2;
            doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
            doc.rect(tableStartX, yPosition, pageWidth - 2 * margin, rowHeight, 'F');
            doc.setLineWidth(0.5);
            doc.line(tableStartX, yPosition, tableStartX + (pageWidth - 2 * margin), yPosition);
            doc.line(tableStartX, yPosition + rowHeight, tableStartX + (pageWidth - 2 * margin), yPosition + rowHeight);
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(textColor[0], textColor[1], textColor[2]);
            doc.text(category.toUpperCase(), pageWidth / 2, yPosition + 5, { align: 'center' });
            doc.setTextColor(0, 0, 0);
            yPosition += rowHeight;
          }

          drawTableDataRow(item, yPosition, { bg: bgColor, text: textColor });
          yPosition += rowHeight;
        });
        
        const sectionHeight = yPosition - categoryStartY;
        drawSectionBorder(tableStartX, categoryStartY, sectionHeight, 0.8);
      });
    } else {
      const quickSectionStartY = yPosition;
      predefinedItems.forEach((item) => {
        if (yPosition + rowHeight > pageHeight - footerHeight) {
          addFooter(doc, pageWidth, pageHeight, footerHeight);
          doc.addPage();
          yPosition = headerHeight;
          tableStartY = yPosition;
          drawTableHeader(tableStartY);
          yPosition += rowHeight;
          
          const newQuickSectionStartY = yPosition;
        }

        drawTableDataRow(item, yPosition);
        yPosition += rowHeight;
      });
      
      const quickSectionHeight = yPosition - quickSectionStartY;
      if (quickSectionHeight > 0) {
        drawSectionBorder(tableStartX, quickSectionStartY, quickSectionHeight, 0.8);
      }
    }

    // Notes section
    if (yPosition + 30 > pageHeight - footerHeight) {
      addFooter(doc, pageWidth, pageHeight, footerHeight);
      doc.addPage();
      yPosition = headerHeight;
    } else {
      yPosition += 5;
    }
    
    doc.setLineWidth(0.5);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Notes:', margin, yPosition);
    yPosition += 8;

    // Notes section without lines - just blank space
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    // Leave blank space for notes (no underline lines)
    yPosition += 20; // Add some space for notes

    addFooter(doc, pageWidth, pageHeight, footerHeight);

    // Save PDF with EncycGames prefix
    let fileNamePrefix = '';
    if (normalizedAssessmentType === 'master-training') {
      fileNamePrefix = trainingLevel ? `${trainingLevel.charAt(0).toUpperCase()}${trainingLevel.slice(1)}` : 'Master-Training';
    } else if (assessmentType === 'high-level') {
      fileNamePrefix = 'High-level';
    } else if (assessmentType === 'fitness') {
      fileNamePrefix = 'Fitness';
    } else if (assessmentType === 'drill') {
      fileNamePrefix = 'Drill';
    }
    const fileName = `EncycGames_Manual_${fileNamePrefix}_Assessment_Form_${playerName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  } catch (error) {
    console.error('Error generating manual assessment form:', error);
    alert(`Error generating form: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

